/**
 * ActionConfigurator Component
 *
 * Configures individual action within a process.
 * Different form for each action type.
 * Uses Standard Component Library throughout.
 *
 * @component
 */

import React, { useState, useEffect } from 'react';
import {
  Plus,
  Trash2,
  Save,
  X,
  Edit3,
  Mail,
  ClipboardList,
  Database,
  MessageSquare,
  CheckSquare,
  Webhook,
  GitBranch,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import {
  StandardButton,
  StandardModal,
  StandardInput,
  StandardSelect,
  StandardTextarea,
  StandardCheckbox,
  StandardCard
} from '../../standard';
import type {
  ActionType,
  ProcessAction,
  ProcessActionInput,
  FieldUpdateConfig,
  CreateRecordConfig,
  SendEmailConfig,
  CreateTaskConfig,
  PostToFeedConfig,
  SubmitForApprovalConfig,
  InvokeProcessConfig,
  UpdateRelatedConfig
} from '../../../types/processBuilder';

// ============================================
// INTERFACES
// ============================================

interface ActionConfiguratorProps {
  action?: ProcessActionInput;
  onSave: (action: ProcessActionInput) => void;
  onCancel: () => void;
  objectType: string;
  isOpen: boolean;
  editMode?: boolean;
}

interface ActionListProps {
  actions: ProcessActionInput[];
  onChange: (actions: ProcessActionInput[]) => void;
  objectType: string;
  title?: string;
  description?: string;
  disabled?: boolean;
}

// ============================================
// CONSTANTS
// ============================================

const ACTION_TYPE_OPTIONS: { value: ActionType; label: string; icon: React.ReactNode; description: string }[] = [
  {
    value: 'field_update',
    label: 'Update Fields',
    icon: <Edit3 size={16} />,
    description: 'Update field values on the current or related record'
  },
  {
    value: 'create_record',
    label: 'Create Record',
    icon: <Database size={16} />,
    description: 'Create a new record of any object type'
  },
  {
    value: 'send_email',
    label: 'Send Email',
    icon: <Mail size={16} />,
    description: 'Send an email using a template or custom content'
  },
  {
    value: 'create_task',
    label: 'Create Task',
    icon: <ClipboardList size={16} />,
    description: 'Create a task assigned to a user or queue'
  },
  {
    value: 'post_to_feed',
    label: 'Post to Feed',
    icon: <MessageSquare size={16} />,
    description: 'Post a message to Chatter or activity feed'
  },
  {
    value: 'submit_for_approval',
    label: 'Submit for Approval',
    icon: <CheckSquare size={16} />,
    description: 'Submit the record for approval'
  },
  {
    value: 'invoke_process',
    label: 'Invoke Process',
    icon: <GitBranch size={16} />,
    description: 'Call another process'
  }
];

const OBJECT_TYPE_OPTIONS = [
  { value: 'Lead', label: 'Lead' },
  { value: 'Contact', label: 'Contact' },
  { value: 'Account', label: 'Account' },
  { value: 'Opportunity', label: 'Opportunity' },
  { value: 'Case', label: 'Case' },
  { value: 'Task', label: 'Task' },
  { value: 'Event', label: 'Event' },
  { value: 'Contract', label: 'Contract' }
];

const FIELD_OPTIONS_BY_OBJECT: Record<string, { value: string; label: string }[]> = {
  Lead: [
    { value: 'Status', label: 'Status' },
    { value: 'LeadScore', label: 'Lead Score' },
    { value: 'Rating', label: 'Rating' },
    { value: 'OwnerId', label: 'Owner' }
  ],
  Opportunity: [
    { value: 'Stage', label: 'Stage' },
    { value: 'Amount', label: 'Amount' },
    { value: 'Probability', label: 'Probability' },
    { value: 'OwnerId', label: 'Owner' }
  ],
  Case: [
    { value: 'Status', label: 'Status' },
    { value: 'Priority', label: 'Priority' },
    { value: 'IsEscalated', label: 'Is Escalated' },
    { value: 'OwnerId', label: 'Owner' }
  ],
  Account: [
    { value: 'Type', label: 'Type' },
    { value: 'IsKeyAccount', label: 'Key Account' },
    { value: 'TotalRevenue', label: 'Total Revenue' }
  ],
  Task: [
    { value: 'Subject', label: 'Subject' },
    { value: 'Status', label: 'Status' },
    { value: 'Priority', label: 'Priority' }
  ],
  Contact: [
    { value: 'WelcomeEmailSent', label: 'Welcome Email Sent' },
    { value: 'WelcomeEmailDate', label: 'Welcome Email Date' }
  ],
  Contract: [
    { value: 'Status', label: 'Status' }
  ],
  default: [
    { value: 'Status', label: 'Status' },
    { value: 'OwnerId', label: 'Owner' }
  ]
};

const EMAIL_TEMPLATE_OPTIONS = [
  { value: '', label: 'Select template...' },
  { value: 'vip_thank_you_template', label: 'VIP Thank You' },
  { value: 'standard_thank_you_template', label: 'Standard Thank You' },
  { value: 'case_escalation_template', label: 'Case Escalation Notification' },
  { value: 'contract_renewal_30day', label: '30 Day Renewal Reminder' },
  { value: 'customer_welcome_template', label: 'Customer Welcome' },
  { value: 'prospect_welcome_template', label: 'Prospect Welcome' }
];

const RECIPIENT_TYPE_OPTIONS = [
  { value: 'owner', label: 'Record Owner' },
  { value: 'user', label: 'Specific User' },
  { value: 'related_user', label: 'Related User Field' },
  { value: 'email_field', label: 'Email Field on Record' },
  { value: 'literal', label: 'Specific Email Address' }
];

const ASSIGN_TO_OPTIONS = [
  { value: 'owner', label: 'Record Owner' },
  { value: 'user', label: 'Specific User' },
  { value: 'related_user', label: 'Related User Field' },
  { value: 'queue', label: 'Queue' }
];

const DUE_DATE_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'tomorrow', label: 'Tomorrow' },
  { value: 'days_from_now', label: 'Days from Now' },
  { value: 'field_value', label: 'Field Value' }
];

const PRIORITY_OPTIONS = [
  { value: 'High', label: 'High' },
  { value: 'Normal', label: 'Normal' },
  { value: 'Low', label: 'Low' }
];

const UPDATE_TYPE_OPTIONS = [
  { value: 'literal', label: 'Specific Value' },
  { value: 'field_reference', label: 'Field Reference' },
  { value: 'formula', label: 'Formula' },
  { value: 'null', label: 'Set to Null' }
];

// ============================================
// HELPER FUNCTIONS
// ============================================

const generateId = (): string => {
  return `act_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

const getFieldOptions = (objectType: string) => {
  return FIELD_OPTIONS_BY_OBJECT[objectType] || FIELD_OPTIONS_BY_OBJECT.default;
};

const getDefaultConfig = (actionType: ActionType): any => {
  switch (actionType) {
    case 'field_update':
      return {
        targetObject: 'current',
        fieldUpdates: [{ field: '', updateType: 'literal', value: '' }]
      } as FieldUpdateConfig;
    case 'create_record':
      return {
        objectType: '',
        fieldMappings: []
      } as CreateRecordConfig;
    case 'send_email':
      return {
        recipientType: 'owner',
        recipient: '',
        templateId: ''
      } as SendEmailConfig;
    case 'create_task':
      return {
        subject: '',
        assignToType: 'owner',
        dueDateType: 'tomorrow',
        priority: 'Normal'
      } as CreateTaskConfig;
    case 'post_to_feed':
      return {
        feedTarget: 'record',
        postContent: ''
      } as PostToFeedConfig;
    case 'submit_for_approval':
      return {
        submitterType: 'current_user'
      } as SubmitForApprovalConfig;
    case 'invoke_process':
      return {
        processId: '',
        recordIdType: 'current'
      } as InvokeProcessConfig;
    default:
      return {};
  }
};

// ============================================
// ACTION TYPE SELECTOR
// ============================================

const ActionTypeSelector: React.FC<{
  value: ActionType;
  onChange: (type: ActionType) => void;
}> = ({ value, onChange }) => (
  <div className="grid grid-cols-2 gap-3">
    {ACTION_TYPE_OPTIONS.map(option => (
      <button
        key={option.value}
        type="button"
        onClick={() => onChange(option.value)}
        className={`flex items-start gap-3 p-3 rounded-lg border-2 text-left transition-all ${
          value === option.value
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
            : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
        }`}
      >
        <div className={`flex-shrink-0 p-2 rounded-lg ${
          value === option.value
            ? 'bg-blue-100 text-blue-700 dark:bg-blue-800 dark:text-blue-300'
            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
        }`}>
          {option.icon}
        </div>
        <div>
          <div className={`font-medium ${
            value === option.value
              ? 'text-blue-900 dark:text-blue-100'
              : 'text-gray-900 dark:text-gray-100'
          }`}>
            {option.label}
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {option.description}
          </div>
        </div>
      </button>
    ))}
  </div>
);

// ============================================
// FIELD UPDATE CONFIG FORM
// ============================================

const FieldUpdateForm: React.FC<{
  config: FieldUpdateConfig;
  onChange: (config: FieldUpdateConfig) => void;
  objectType: string;
}> = ({ config, onChange, objectType }) => {
  const addFieldUpdate = () => {
    onChange({
      ...config,
      fieldUpdates: [...config.fieldUpdates, { field: '', updateType: 'literal', value: '' }]
    });
  };

  const removeFieldUpdate = (index: number) => {
    const updated = config.fieldUpdates.filter((_, i) => i !== index);
    onChange({ ...config, fieldUpdates: updated });
  };

  const updateFieldUpdate = (index: number, field: string, value: any) => {
    const updated = [...config.fieldUpdates];
    updated[index] = { ...updated[index], [field]: value };
    onChange({ ...config, fieldUpdates: updated });
  };

  const targetObjectType = config.targetObject === 'current' ? objectType : config.relatedObjectType || objectType;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <StandardSelect
          label="Target Object"
          value={config.targetObject}
          onChange={(e) => onChange({ ...config, targetObject: e.target.value as 'current' | 'related' })}
          options={[
            { value: 'current', label: 'Current Record' },
            { value: 'related', label: 'Related Record' }
          ]}
        />
        {config.targetObject === 'related' && (
          <StandardInput
            label="Related Field"
            value={config.relatedField || ''}
            onChange={(e) => onChange({ ...config, relatedField: e.target.value })}
            placeholder="e.g., AccountId"
          />
        )}
      </div>

      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Field Updates
        </label>
        {config.fieldUpdates.map((fu, index) => (
          <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
            <div className="flex-1">
              <StandardSelect
                value={fu.field}
                onChange={(e) => updateFieldUpdate(index, 'field', e.target.value)}
                options={[
                  { value: '', label: 'Select field...' },
                  ...getFieldOptions(targetObjectType)
                ]}
              />
            </div>
            <div className="flex-1">
              <StandardSelect
                value={fu.updateType}
                onChange={(e) => updateFieldUpdate(index, 'updateType', e.target.value)}
                options={UPDATE_TYPE_OPTIONS}
              />
            </div>
            {fu.updateType !== 'null' && (
              <div className="flex-1">
                <StandardInput
                  value={fu.value}
                  onChange={(e) => updateFieldUpdate(index, 'value', e.target.value)}
                  placeholder="Value"
                />
              </div>
            )}
            <StandardButton
              size="xs"
              variant="ghost"
              onClick={() => removeFieldUpdate(index)}
            >
              <Trash2 size={14} className="text-red-500" />
            </StandardButton>
          </div>
        ))}
        <StandardButton
          size="sm"
          variant="secondary"
          onClick={addFieldUpdate}
          leftIcon={<Plus size={14} />}
        >
          Add Field
        </StandardButton>
      </div>
    </div>
  );
};

// ============================================
// CREATE RECORD CONFIG FORM
// ============================================

const CreateRecordForm: React.FC<{
  config: CreateRecordConfig;
  onChange: (config: CreateRecordConfig) => void;
}> = ({ config, onChange }) => {
  const addFieldMapping = () => {
    onChange({
      ...config,
      fieldMappings: [...config.fieldMappings, { field: '', valueType: 'literal', value: '' }]
    });
  };

  const removeFieldMapping = (index: number) => {
    const updated = config.fieldMappings.filter((_, i) => i !== index);
    onChange({ ...config, fieldMappings: updated });
  };

  const updateFieldMapping = (index: number, field: string, value: any) => {
    const updated = [...config.fieldMappings];
    updated[index] = { ...updated[index], [field]: value };
    onChange({ ...config, fieldMappings: updated });
  };

  return (
    <div className="space-y-4">
      <StandardSelect
        label="Object Type to Create"
        value={config.objectType}
        onChange={(e) => onChange({ ...config, objectType: e.target.value })}
        options={[
          { value: '', label: 'Select object type...' },
          ...OBJECT_TYPE_OPTIONS
        ]}
      />

      {config.objectType && (
        <div className="space-y-3">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
            Field Mappings
          </label>
          {config.fieldMappings.map((fm, index) => (
            <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
              <div className="flex-1">
                <StandardSelect
                  value={fm.field}
                  onChange={(e) => updateFieldMapping(index, 'field', e.target.value)}
                  options={[
                    { value: '', label: 'Select field...' },
                    ...getFieldOptions(config.objectType)
                  ]}
                />
              </div>
              <div className="flex-1">
                <StandardSelect
                  value={fm.valueType}
                  onChange={(e) => updateFieldMapping(index, 'valueType', e.target.value)}
                  options={UPDATE_TYPE_OPTIONS.filter(o => o.value !== 'null')}
                />
              </div>
              <div className="flex-1">
                <StandardInput
                  value={fm.value}
                  onChange={(e) => updateFieldMapping(index, 'value', e.target.value)}
                  placeholder="Value"
                />
              </div>
              <StandardButton
                size="xs"
                variant="ghost"
                onClick={() => removeFieldMapping(index)}
              >
                <Trash2 size={14} className="text-red-500" />
              </StandardButton>
            </div>
          ))}
          <StandardButton
            size="sm"
            variant="secondary"
            onClick={addFieldMapping}
            leftIcon={<Plus size={14} />}
          >
            Add Field Mapping
          </StandardButton>
        </div>
      )}
    </div>
  );
};

// ============================================
// SEND EMAIL CONFIG FORM
// ============================================

const SendEmailForm: React.FC<{
  config: SendEmailConfig;
  onChange: (config: SendEmailConfig) => void;
}> = ({ config, onChange }) => (
  <div className="space-y-4">
    <StandardSelect
      label="Email Template"
      value={config.templateId || ''}
      onChange={(e) => onChange({ ...config, templateId: e.target.value })}
      options={EMAIL_TEMPLATE_OPTIONS}
    />

    <div className="grid grid-cols-2 gap-4">
      <StandardSelect
        label="Recipient Type"
        value={config.recipientType}
        onChange={(e) => onChange({ ...config, recipientType: e.target.value as any })}
        options={RECIPIENT_TYPE_OPTIONS}
      />
      <StandardInput
        label="Recipient"
        value={config.recipient}
        onChange={(e) => onChange({ ...config, recipient: e.target.value })}
        placeholder={config.recipientType === 'literal' ? 'email@example.com' : 'Field reference'}
      />
    </div>

    {!config.templateId && (
      <>
        <StandardInput
          label="Subject"
          value={config.subject || ''}
          onChange={(e) => onChange({ ...config, subject: e.target.value })}
          placeholder="Email subject"
        />
        <StandardTextarea
          label="Body"
          value={config.body || ''}
          onChange={(e) => onChange({ ...config, body: e.target.value })}
          placeholder="Email body content"
          rows={4}
        />
      </>
    )}
  </div>
);

// ============================================
// CREATE TASK CONFIG FORM
// ============================================

const CreateTaskForm: React.FC<{
  config: CreateTaskConfig;
  onChange: (config: CreateTaskConfig) => void;
}> = ({ config, onChange }) => (
  <div className="space-y-4">
    <StandardInput
      label="Subject"
      value={config.subject}
      onChange={(e) => onChange({ ...config, subject: e.target.value })}
      placeholder="Task subject (supports merge fields)"
      required
    />

    <div className="grid grid-cols-2 gap-4">
      <StandardSelect
        label="Assign To"
        value={config.assignToType}
        onChange={(e) => onChange({ ...config, assignToType: e.target.value as any })}
        options={ASSIGN_TO_OPTIONS}
      />
      {config.assignToType !== 'owner' && (
        <StandardInput
          label="Assignee"
          value={config.assignTo || ''}
          onChange={(e) => onChange({ ...config, assignTo: e.target.value })}
          placeholder="User ID or field reference"
        />
      )}
    </div>

    <div className="grid grid-cols-2 gap-4">
      <StandardSelect
        label="Due Date"
        value={config.dueDateType}
        onChange={(e) => onChange({ ...config, dueDateType: e.target.value as any })}
        options={DUE_DATE_OPTIONS}
      />
      {config.dueDateType === 'days_from_now' && (
        <StandardInput
          label="Days"
          type="number"
          value={String(config.dueDays || '')}
          onChange={(e) => onChange({ ...config, dueDays: parseInt(e.target.value) || 0 })}
          placeholder="Number of days"
        />
      )}
    </div>

    <StandardSelect
      label="Priority"
      value={config.priority}
      onChange={(e) => onChange({ ...config, priority: e.target.value as any })}
      options={PRIORITY_OPTIONS}
    />

    <StandardTextarea
      label="Description"
      value={config.description || ''}
      onChange={(e) => onChange({ ...config, description: e.target.value })}
      placeholder="Task description"
      rows={3}
    />
  </div>
);

// ============================================
// POST TO FEED CONFIG FORM
// ============================================

const PostToFeedForm: React.FC<{
  config: PostToFeedConfig;
  onChange: (config: PostToFeedConfig) => void;
}> = ({ config, onChange }) => (
  <div className="space-y-4">
    <StandardSelect
      label="Post To"
      value={config.feedTarget}
      onChange={(e) => onChange({ ...config, feedTarget: e.target.value as any })}
      options={[
        { value: 'record', label: 'Record Feed' },
        { value: 'user', label: 'User Feed' },
        { value: 'group', label: 'Group Feed' }
      ]}
    />

    {config.feedTarget !== 'record' && (
      <StandardInput
        label="Target ID"
        value={config.targetId || ''}
        onChange={(e) => onChange({ ...config, targetId: e.target.value })}
        placeholder="User or Group ID"
      />
    )}

    <StandardTextarea
      label="Post Content"
      value={config.postContent}
      onChange={(e) => onChange({ ...config, postContent: e.target.value })}
      placeholder="Post content (supports merge fields)"
      rows={3}
      required
    />

    <StandardCheckbox
      label="Mention Record Owner"
      checked={config.mentionOwner || false}
      onChange={(checked) => onChange({ ...config, mentionOwner: checked })}
    />
  </div>
);

// ============================================
// SUBMIT FOR APPROVAL CONFIG FORM
// ============================================

const SubmitForApprovalForm: React.FC<{
  config: SubmitForApprovalConfig;
  onChange: (config: SubmitForApprovalConfig) => void;
}> = ({ config, onChange }) => (
  <div className="space-y-4">
    <StandardSelect
      label="Approval Process"
      value={config.approvalProcessId || ''}
      onChange={(e) => onChange({ ...config, approvalProcessId: e.target.value })}
      options={[
        { value: '', label: 'Default Process' },
        { value: 'ap_1', label: 'Opportunity Discount Approval' },
        { value: 'ap_2', label: 'Contract Approval' },
        { value: 'ap_3', label: 'Expense Approval' }
      ]}
    />

    <StandardSelect
      label="Submitter"
      value={config.submitterType}
      onChange={(e) => onChange({ ...config, submitterType: e.target.value as any })}
      options={[
        { value: 'current_user', label: 'Current User' },
        { value: 'owner', label: 'Record Owner' },
        { value: 'user', label: 'Specific User' }
      ]}
    />

    <StandardTextarea
      label="Submission Comments"
      value={config.comments || ''}
      onChange={(e) => onChange({ ...config, comments: e.target.value })}
      placeholder="Optional comments"
      rows={2}
    />
  </div>
);

// ============================================
// INVOKE PROCESS CONFIG FORM
// ============================================

const InvokeProcessForm: React.FC<{
  config: InvokeProcessConfig;
  onChange: (config: InvokeProcessConfig) => void;
}> = ({ config, onChange }) => (
  <div className="space-y-4">
    <StandardSelect
      label="Process to Invoke"
      value={config.processId}
      onChange={(e) => onChange({ ...config, processId: e.target.value })}
      options={[
        { value: '', label: 'Select process...' },
        { value: 'proc_1', label: 'Lead Nurture Process' },
        { value: 'proc_2', label: 'Account Update Process' }
      ]}
    />

    <StandardSelect
      label="Record ID Source"
      value={config.recordIdType}
      onChange={(e) => onChange({ ...config, recordIdType: e.target.value as any })}
      options={[
        { value: 'current', label: 'Current Record' },
        { value: 'related', label: 'Related Record' },
        { value: 'created', label: 'Newly Created Record' }
      ]}
    />

    {config.recordIdType !== 'current' && (
      <StandardInput
        label="Record ID Field"
        value={config.recordIdField || ''}
        onChange={(e) => onChange({ ...config, recordIdField: e.target.value })}
        placeholder="Field containing record ID"
      />
    )}
  </div>
);

// ============================================
// MAIN CONFIGURATOR MODAL
// ============================================

export const ActionConfigurator: React.FC<ActionConfiguratorProps> = ({
  action,
  onSave,
  onCancel,
  objectType,
  isOpen,
  editMode = false
}) => {
  const [actionType, setActionType] = useState<ActionType>(action?.type || 'field_update');
  const [name, setName] = useState(action?.name || '');
  const [description, setDescription] = useState(action?.description || '');
  const [config, setConfig] = useState<any>(action?.config || getDefaultConfig('field_update'));

  // Reset form when action changes
  useEffect(() => {
    if (action) {
      setActionType(action.type);
      setName(action.name);
      setDescription(action.description || '');
      setConfig(action.config);
    } else {
      setActionType('field_update');
      setName('');
      setDescription('');
      setConfig(getDefaultConfig('field_update'));
    }
  }, [action, isOpen]);

  const handleTypeChange = (type: ActionType) => {
    setActionType(type);
    setConfig(getDefaultConfig(type));
  };

  const handleSave = () => {
    const newAction: ProcessActionInput = {
      id: action?.id,
      type: actionType,
      name: name || `${ACTION_TYPE_OPTIONS.find(a => a.value === actionType)?.label} Action`,
      description,
      order: action?.order || 0,
      config
    };
    onSave(newAction);
  };

  const renderConfigForm = () => {
    switch (actionType) {
      case 'field_update':
        return <FieldUpdateForm config={config} onChange={setConfig} objectType={objectType} />;
      case 'create_record':
        return <CreateRecordForm config={config} onChange={setConfig} />;
      case 'send_email':
        return <SendEmailForm config={config} onChange={setConfig} />;
      case 'create_task':
        return <CreateTaskForm config={config} onChange={setConfig} />;
      case 'post_to_feed':
        return <PostToFeedForm config={config} onChange={setConfig} />;
      case 'submit_for_approval':
        return <SubmitForApprovalForm config={config} onChange={setConfig} />;
      case 'invoke_process':
        return <InvokeProcessForm config={config} onChange={setConfig} />;
      default:
        return null;
    }
  };

  return (
    <StandardModal
      isOpen={isOpen}
      onClose={onCancel}
      title={editMode ? 'Edit Action' : 'Add Action'}
      size="lg"
    >
      <StandardModal.Body>
        <div className="space-y-6">
          {/* Action Type Selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
              Action Type
            </label>
            <ActionTypeSelector value={actionType} onChange={handleTypeChange} />
          </div>

          {/* Action Name & Description */}
          <div className="grid grid-cols-2 gap-4">
            <StandardInput
              label="Action Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Give this action a name"
            />
            <StandardInput
              label="Description (Optional)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this action does"
            />
          </div>

          {/* Action-specific Configuration */}
          <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">
              Configure {ACTION_TYPE_OPTIONS.find(a => a.value === actionType)?.label}
            </h4>
            {renderConfigForm()}
          </div>
        </div>
      </StandardModal.Body>
      <StandardModal.Footer>
        <StandardButton variant="secondary" onClick={onCancel}>
          Cancel
        </StandardButton>
        <StandardButton
          variant="primary"
          onClick={handleSave}
          leftIcon={<Save size={16} />}
        >
          {editMode ? 'Save Changes' : 'Add Action'}
        </StandardButton>
      </StandardModal.Footer>
    </StandardModal>
  );
};

// ============================================
// ACTION LIST COMPONENT
// ============================================

export const ActionList: React.FC<ActionListProps> = ({
  actions,
  onChange,
  objectType,
  title,
  description,
  disabled
}) => {
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const [editingAction, setEditingAction] = useState<ProcessActionInput | undefined>();
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const handleAddAction = () => {
    setEditingAction(undefined);
    setEditingIndex(null);
    setIsConfigOpen(true);
  };

  const handleEditAction = (action: ProcessActionInput, index: number) => {
    setEditingAction(action);
    setEditingIndex(index);
    setIsConfigOpen(true);
  };

  const handleSaveAction = (action: ProcessActionInput) => {
    if (editingIndex !== null) {
      // Update existing
      const updated = [...actions];
      updated[editingIndex] = action;
      onChange(updated);
    } else {
      // Add new
      onChange([...actions, { ...action, order: actions.length }]);
    }
    setIsConfigOpen(false);
    setEditingAction(undefined);
    setEditingIndex(null);
  };

  const handleRemoveAction = (index: number) => {
    onChange(actions.filter((_, i) => i !== index));
  };

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const updated = [...actions];
    [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
    onChange(updated);
  };

  const handleMoveDown = (index: number) => {
    if (index === actions.length - 1) return;
    const updated = [...actions];
    [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
    onChange(updated);
  };

  const getActionIcon = (type: ActionType) => {
    const option = ACTION_TYPE_OPTIONS.find(a => a.value === type);
    return option?.icon || <Edit3 size={16} />;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      {(title || description) && (
        <div>
          {title && (
            <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {title}
            </h4>
          )}
          {description && (
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {description}
            </p>
          )}
        </div>
      )}

      {/* Actions List */}
      <div className="space-y-2">
        {actions.length === 0 ? (
          <div className="text-center py-6 text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-200 dark:border-gray-700 rounded-lg">
            <p className="text-sm">No actions defined</p>
            <p className="text-xs mt-1">Click "Add Action" to configure what happens</p>
          </div>
        ) : (
          actions.map((action, index) => (
            <div
              key={action.id || index}
              className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg group"
            >
              <div className="flex-shrink-0 p-2 rounded-lg bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                {getActionIcon(action.type)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                  {action.name}
                </div>
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {ACTION_TYPE_OPTIONS.find(a => a.value === action.type)?.label}
                </div>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <StandardButton
                  size="xs"
                  variant="ghost"
                  onClick={() => handleMoveUp(index)}
                  disabled={index === 0 || disabled}
                >
                  <ArrowUp size={14} />
                </StandardButton>
                <StandardButton
                  size="xs"
                  variant="ghost"
                  onClick={() => handleMoveDown(index)}
                  disabled={index === actions.length - 1 || disabled}
                >
                  <ArrowDown size={14} />
                </StandardButton>
                <StandardButton
                  size="xs"
                  variant="ghost"
                  onClick={() => handleEditAction(action, index)}
                  disabled={disabled}
                >
                  <Edit3 size={14} />
                </StandardButton>
                <StandardButton
                  size="xs"
                  variant="ghost"
                  onClick={() => handleRemoveAction(index)}
                  disabled={disabled}
                >
                  <Trash2 size={14} className="text-red-500" />
                </StandardButton>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Add Button */}
      <StandardButton
        variant="secondary"
        size="sm"
        onClick={handleAddAction}
        leftIcon={<Plus size={16} />}
        disabled={disabled}
      >
        Add Action
      </StandardButton>

      {/* Configurator Modal */}
      <ActionConfigurator
        isOpen={isConfigOpen}
        action={editingAction}
        onSave={handleSaveAction}
        onCancel={() => setIsConfigOpen(false)}
        objectType={objectType}
        editMode={editingIndex !== null}
      />
    </div>
  );
};

export default ActionConfigurator;
